import * as types from './types';
import { getErrorMessage, parsePagination, isCancel } from 'utils';
import { uiActions } from 'state/modules/UI';
import * as paymentsAPI from 'services/api/payment';
import i18n from 'i18n-js';

// Get payments
const fetchPaymentsRequest = () => ({
  type: types.FETCH_PAYMENTS_REQUEST,
});

const fetchPaymentsSuccess = (payload) => ({
  type: types.FETCH_PAYMENTS_SUCCESS,
  payload,
});

const fetchPaymentsFailed = (error) => ({
  type: types.FETCH_PAYMENTS_FAILED,
  error,
});

export const fetchPayments = (params = {}, options) => {
  return async (dispatch) => {
    dispatch(fetchPaymentsRequest());

    const response = await paymentsAPI.fetchPayments(params, options);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchPaymentsSuccess({
          pagination: pagination,
          data: response.data,
        })
      );
    }

    if (!isCancel(response)) {
      const error = getErrorMessage(response.data);

      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: error,
        })
      );

      dispatch(fetchPaymentsFailed());
    }
  };
};

// Get payments methods
const fetchPaymentMethodsRequest = () => ({
  type: types.FETCH_PAYMENT_METHODS_REQUEST,
});

const fetchPaymentMethodsSuccess = (payload) => ({
  type: types.FETCH_PAYMENT_METHODS_SUCCESS,
  payload,
});

const fetchPaymentMethodsFailed = (error) => ({
  type: types.FETCH_PAYMENT_METHODS_FAILED,
  error,
});

export const fetchPaymentMethods = (params = {}) => {
  return async (dispatch) => {
    dispatch(fetchPaymentMethodsRequest());

    const response = await paymentsAPI.fetchPaymentMethods(params);

    if (response.ok) {
      const pagination = parsePagination(response.headers);

      return dispatch(
        fetchPaymentMethodsSuccess({
          pagination: pagination,
          data: response.data,
        })
      );
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchPaymentMethodsFailed());
  };
};

// Get payment by ID
const fetchPaymentRequest = () => ({
  type: types.FETCH_PAYMENT_BY_ID_REQUEST,
});

const fetchPaymentSuccess = (payload) => ({
  type: types.FETCH_PAYMENT_BY_ID_SUCCESS,
  payload,
});

const fetchPaymentFailed = (error) => ({
  type: types.FETCH_PAYMENT_BY_ID_FAILED,
  error,
});

export const fetchPaymentById = (id) => {
  return async (dispatch) => {
    dispatch(fetchPaymentRequest());

    const response = await paymentsAPI.fetchPaymentById(id);

    if (response.ok) {
      return dispatch(fetchPaymentSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchPaymentFailed(error));
  };
};

const fetchAddPaymentMethodRequest = (payload) => ({
  type: types.FETCH_ADD_PAYMENT_METHOD_REQUEST,
  payload,
});

const fetchAddPaymentMethodSuccess = (payload) => ({
  type: types.FETCH_ADD_PAYMENT_METHOD_SUCCESS,
  payload,
});

const fetchAddPaymentMethodFailed = (payload) => ({
  type: types.FETCH_ADD_PAYMENT_METHOD_FAILED,
  payload,
});

export const fetchAddPaymentMethod = (data) => async (dispatch) => {
  dispatch(fetchAddPaymentMethodRequest());

  const response = await paymentsAPI.addPaymentMethod(data);

  if (response.ok) {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: i18n.t('payment_method.added'),
      })
    );
    return dispatch(fetchAddPaymentMethodSuccess(response.data));
  }

  const formattedError = getErrorMessage(response.data);

  if (typeof formattedError === 'string') {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  dispatch(fetchAddPaymentMethodFailed({ errors: formattedError }));
};

const fetchDeletePaymentMethodRequest = (payload) => ({
  type: types.FETCH_DELETE_PAYMENT_METHOD_REQUEST,
  payload,
});

const fetchDeletePaymentMethodSuccess = (payload) => ({
  type: types.FETCH_DELETE_PAYMENT_METHOD_SUCCESS,
  payload,
});

const fetchDeletePaymentMethodFailed = (payload) => ({
  type: types.FETCH_DELETE_PAYMENT_METHOD_FAILED,
  payload,
});

export const fetchDeletePaymentMethod = (id) => async (dispatch) => {
  dispatch(fetchDeletePaymentMethodRequest());

  const response = await paymentsAPI.deletePaymentMethod(id);

  if (response.ok) {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: i18n.t('payment_method.deleted'),
      })
    );
    return dispatch(fetchDeletePaymentMethodSuccess(response.data));
  }

  const error = getErrorMessage(response.data);

  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: error,
    })
  );

  dispatch(fetchDeletePaymentMethodFailed(response.data));
};

// Update payment method
const fetchUpdatePaymentMethodRequest = () => ({
  type: types.FETCH_UPDATE_PAYMENT_METHOD_REQUEST,
});

const fetchUpdatePaymentMethodSuccess = (payload) => ({
  type: types.FETCH_UPDATE_PAYMENT_METHOD_SUCCESS,
  payload,
});

const fetchUpdatePaymentMethodFailed = () => ({
  type: types.FETCH_UPDATE_PAYMENT_METHOD_FAILED,
});

export const fetchUpdatePaymentMethod = (id, data) => {
  return async (dispatch) => {
    dispatch(fetchUpdatePaymentMethodRequest());

    const response = await paymentsAPI.fetchUpdatePaymentMethod(id, data);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('payment_method.updated'),
        })
      );
      return dispatch(fetchUpdatePaymentMethodSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchUpdatePaymentMethodFailed(error));
  };
};

// Verify payment method
const fetchVerifyPaymentMethodRequest = () => ({
  type: types.FETCH_UPDATE_PAYMENT_METHOD_REQUEST,
});

const fetchVerifyPaymentMethodSuccess = (payload) => ({
  type: types.FETCH_UPDATE_PAYMENT_METHOD_SUCCESS,
  payload,
});

const fetchVerifyPaymentMethodFailed = () => ({
  type: types.FETCH_UPDATE_PAYMENT_METHOD_FAILED,
});

export const fetchVerifyPaymentMethod = (id, data) => {
  return async (dispatch) => {
    dispatch(fetchVerifyPaymentMethodRequest());

    const response = await paymentsAPI.fetchVerifyPaymentMethod(id, data);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('payment_method.updated'),
        })
      );
      return dispatch(fetchVerifyPaymentMethodSuccess(response.data));
    }

    const error = getErrorMessage(response.data);

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );

    dispatch(fetchVerifyPaymentMethodFailed(error));
  };
};

export const resetFetchPaymentMethods = () => ({
  type: types.RESET_FETCH_PAYMENT_METHODS,
});
